import {
  FormControl,
  FormErrorMessage,
  Box,
  Button,
  VStack,
} from "@chakra-ui/react";
import {
  AccountSubType,
  AccountType,
} from "@syla/shared/types/models/AccountBase";
import { AxiosError } from "axios";
import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router";
import isEmail from "validator/lib/isEmail";
import { UserContext } from "../../../contexts/UserContext";
import { useNavigator, Route } from "../../../routers/navigator";
import { SignInFormContainer } from "../../atoms/Containers";
import {
  PasswordRequirements,
  validatePasswordInput,
} from "../../atoms/PasswordRequirements";
import { InputField } from "../../forms/InputField";
import { SingleSelectorField } from "../../forms/SingleSelectorField";
import { GoogleSignInForm } from "../GoogleSignInForm";
import { ACTIVITY_TYPE_OPTIONS, ACCOUNT_TYPE_OPTIONS } from "./accountOptions";
import { SignInAndTermsLinks, SignUpToSyla } from "./FormComponents";

type SignUpFormValues = {
  userEmail: string;
  userPassword: string;
  userAccountType: AccountType;
  userAccountSubType: AccountSubType;
};

export function SignUpForm(): React.ReactElement {
  // get the register function from the user context
  const { register: contextRegister } = useContext(UserContext);
  const location = useLocation();
  const prefillEmail = new URLSearchParams(location.search).get("email") || "";

  // state for sign up error
  const [signUpError, setSignUpError] = useState<string | undefined>();

  const navigate = useNavigator();

  const form = useForm<SignUpFormValues>({
    mode: "onTouched",
    defaultValues: {
      userAccountType: AccountType.Individual,
      userAccountSubType: AccountSubType.Investor,
      userEmail: isEmail(prefillEmail) ? prefillEmail : "",
      userPassword: "",
    },
  });

  const {
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = form;

  const onSubmit = async (data: SignUpFormValues) => {
    setSignUpError(undefined);

    const { userEmail, userPassword, userAccountType, userAccountSubType } =
      data;

    try {
      await contextRegister({
        email: userEmail,
        password: userPassword,
        accountType: userAccountType,
        accountSubType: userAccountSubType,
      });

      navigate({
        route: Route.EmailVerification,
        state: {
          email: userEmail,
        },
      });
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 403) {
        setSignUpError(
          "The email provided is not eligible for an account with Syla",
        );
        return;
      }
      setSignUpError("Unexpected error occurred, please try again later");
    }
  };

  return (
    <SignInFormContainer>
      <Box mb="30px">
        <SignUpToSyla />
      </Box>
      <GoogleSignInForm />

      <Box as="form" w="full" onSubmit={handleSubmit(onSubmit)} noValidate>
        <VStack width="full" spacing={6} align="stretch">
          <SingleSelectorField
            form={form}
            name="userAccountType"
            label="Account type"
            isRequired
            options={ACCOUNT_TYPE_OPTIONS}
            placeholder="Select an account type"
            buttonProps={{
              width: "full",
              backgroundColor: "white",
            }}
            popoverProps={{
              matchWidth: true,
            }}
          />

          <SingleSelectorField
            form={form}
            name="userAccountSubType"
            label="Activity type"
            isRequired
            options={ACTIVITY_TYPE_OPTIONS}
            placeholder="Select an activity type"
            buttonProps={{
              width: "full",
              backgroundColor: "white",
            }}
            popoverProps={{
              matchWidth: true,
            }}
          />

          <InputField
            form={form}
            name="userEmail"
            label="Email address"
            placeholder="Enter your email"
            type="email"
            isRequired
            autoFocus
            backgroundColor="white"
            autoComplete="email"
            rules={{
              validate: (email?: string) =>
                isEmail(email || "") ? true : "Invalid email address",
            }}
          />

          <InputField
            form={form}
            name="userPassword"
            label="Password"
            placeholder="Enter a password"
            type="password"
            helperText={<PasswordRequirements />}
            isRequired
            backgroundColor="white"
            autoComplete="new-password"
            rules={{
              validate: validatePasswordInput,
            }}
          />

          <Button
            variant="solid"
            colorScheme="primary"
            isDisabled={isSubmitting || !isValid}
            isLoading={isSubmitting}
            width="full"
            type="submit"
          >
            Sign up
          </Button>

          {signUpError && (
            <FormControl isInvalid={!!signUpError}>
              <FormErrorMessage>{signUpError}</FormErrorMessage>
            </FormControl>
          )}
          <SignInAndTermsLinks disabled={isSubmitting} />
        </VStack>
      </Box>
    </SignInFormContainer>
  );
}
